import { Moon, Sun } from "lucide-react";

import { useT } from "../../i18n";
import { useDataStore } from "../../store/dataStore";
import { useThemeStore } from "../../store/themeStore";
import { HeaderStats } from "../stats/HeaderStats";
import { IconButton } from "../ui/IconButton";
import { LanguageControl } from "./LanguageControl";

/**
 * The logo is served as two files, dark-on-transparent and white-on-
 * transparent, because it carries its own brand colors (deep blue + gold)
 * that the brief keeps out of the UI palette. We swap the asset with the
 * theme rather than recoloring one image.
 */
function Logo() {
  const resolved = useThemeStore((s) => s.resolved);
  const src = resolved === "dark" ? "/brand/kgs-logo-dark.png" : "/brand/kgs-logo-light.png";
  return <img src={src} alt="KGS" className="h-9 w-auto" />;
}

export function Header() {
  const t = useT();
  const resolved = useThemeStore((s) => s.resolved);
  const toggle = useThemeStore((s) => s.toggle);
  // The backend says whether it is serving synthetic detections (MAP_DATASET).
  // A badge that is always on is a badge nobody reads, and this one is the only
  // thing standing between a demo screenshot and a decision made on it.
  const isDemo = useDataStore((s) => s.meta?.dataset === "demo");

  return (
    <header data-map-overlay="true" className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1 border-b border-border-subtle bg-surface-raised/80 px-4 py-2 shadow-md backdrop-blur">
      <div className="flex min-w-0 items-center gap-3">
        <Logo />
        {isDemo && (
          <span
            title={t.app.demoTitle}
            className="shrink-0 rounded-full bg-status-warningBg px-2 py-0.5 text-xs font-semibold uppercase tracking-wide text-status-warning"
          >
            {t.app.demoBadge}
          </span>
        )}
        <div className="min-w-0 leading-tight">
          {/* Brand name carries the accent (морская волна) as a pointwise
              signature — #3E7E7E in light, its lighter pair #5FA3A3 in dark.
              The tagline stays quiet: an uppercase, tracked, secondary label
              (secondary, not tertiary, so it keeps ≥4.5:1 in both themes). */}
          <h1 className="truncate text-lg font-semibold tracking-tight text-accent">
            {t.app.title}
          </h1>
          {/* Tagline hidden on the narrowest screens to save the row. */}
          <p className="hidden truncate text-[0.6875rem] font-medium uppercase tracking-[0.09em] text-text-secondary sm:block">
            {t.app.subtitle}
          </p>
        </div>
      </div>

      <div className="flex items-center gap-3 sm:gap-4">
        <HeaderStats />
        <LanguageControl />
        <IconButton
          label={
            resolved === "dark"
              ? t.header.themeToggleToLight
              : t.header.themeToggleToDark
          }
          onClick={toggle}
        >
          {resolved === "dark" ? <Sun size={18} /> : <Moon size={18} />}
        </IconButton>
      </div>
    </header>
  );
}
